import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { PageHeader } from "@/components/DashboardLayout";
import ReferralModal from "@/components/ReferralModal";
import WeeklyCadenceChart from "@/pages/affiliate/WeeklyCadenceChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    Gift,
    Copy,
    QrCode,
    TrendingUp,
    Users,
    Trophy,
    Link2,
    Download,
    Package,
    Wallet,
    Clock,
    ArrowRight,
    AlertCircle,
} from "lucide-react";

/**
 * Affiliate dashboard — the landing page for role=affiliate users.
 *
 * Reuses the referral engine built for members (same
 * `/member/referrals/*` endpoints, now role-widened to accept affiliates).
 */
export default function AffiliateDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [ref, setRef] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deckBusy, setDeckBusy] = useState(false);
    const [payments, setPayments] = useState(null);

    const load = async () => {
        try {
            const [r1, r2, r3] = await Promise.all([
                api.post("/member/referrals/mine"),
                api.get("/member/referrals/stats"),
                api.get("/affiliate/payments"),
            ]);
            setRef(r1.data);
            setStats(r2.data);
            setPayments(r3.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const shareUrl = ref
        ? `${window.location.origin}/join?ref=${ref.code}`
        : "";

    const copy = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copied`);
        } catch {
            toast.error("Couldn't copy — please copy manually.");
        }
    };

    const downloadPandaDeck = async () => {
        setDeckBusy(true);
        try {
            const res = await api.get("/member/referrals/promo-pack", {
                responseType: "blob",
            });
            const disposition = res.headers?.["content-disposition"] || "";
            const match = /filename="?([^";]+)"?/.exec(disposition);
            const filename = match ? match[1] : "perx-panda-deck.zip";
            const blob = new Blob([res.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Panda Deck downloaded — happy posting.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeckBusy(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Affiliate Portal"
                title={`Hello ${user?.name?.split(" ")[0] || "friend"} 👋`}
                description="Everything you need to promote Perx to your audience — your referral link, QR code, discount codes and live conversion stats."
                testid="affiliate-dashboard-header"
                action={
                    <Button
                        onClick={() => setModalOpen(true)}
                        data-testid="affiliate-open-modal-btn"
                        className="rounded-full h-11 px-6 bg-navy-700 hover:bg-navy-800 text-cream"
                    >
                        <Gift size={16} className="mr-2" />
                        Share &amp; email invites
                    </Button>
                }
            />
            <div className="px-6 lg:px-12 py-10 space-y-8">
                {/* KPI strip */}
                <div className="grid sm:grid-cols-4 gap-4">
                    <StatCard
                        icon={Users}
                        label="Signups"
                        value={stats?.signups_count ?? 0}
                        blurb="People who created a Perx account via your link"
                    />
                    <StatCard
                        icon={TrendingUp}
                        label="Conversions"
                        value={stats?.conversions_count ?? 0}
                        blurb="Signed up + purchased a Perx membership"
                    />
                    <StatCard
                        icon={Trophy}
                        label="Current reward"
                        value={
                            stats?.admin_override_tier
                                ? `${stats.admin_override_tier.discount_pct}% off`
                                : stats?.current_tier
                                ? `${stats.current_tier.discount_pct}% off`
                                : "—"
                        }
                        blurb={
                            stats?.admin_override_tier
                                ? "Admin override active"
                                : stats?.current_tier
                                ? stats.current_tier.name
                                : "Reach the first tier to unlock"
                        }
                    />
                    <StatCard
                        icon={Gift}
                        label="Next tier"
                        value={
                            stats?.next_tier
                                ? `${stats.next_tier.threshold - (stats.conversions_count ?? 0)} to go`
                                : "Top tier"
                        }
                        blurb={
                            stats?.next_tier
                                ? `Unlocks ${stats.next_tier.discount_pct}% off`
                                : "You've reached the highest reward"
                        }
                    />
                </div>

                {/* Payment summary card */}
                <PaymentSummaryCard payments={payments} />

                {/* Weekly analytics chart */}
                <WeeklyCadenceChart />

                {/* Toolkit card */}
                <div
                    data-testid="affiliate-toolkit"
                    className="rounded-3xl border border-navy-100 bg-white p-6 lg:p-8"
                >
                    <div className="flex items-center gap-2">
                        <Link2 size={18} className="text-gold-600" />
                        <h2 className="font-display text-2xl text-navy-700">
                            Your affiliate toolkit
                        </h2>
                    </div>
                    <p className="text-sm text-navy-500 mt-1">
                        Every friend who signs up through your link and buys a
                        membership counts as a converted referral.
                    </p>

                    <div className="mt-6 grid lg:grid-cols-3 gap-4">
                        <div className="rounded-2xl bg-cream border border-navy-100 p-5">
                            <div className="text-[11px] uppercase tracking-widest text-gold-600">
                                Referral code
                            </div>
                            <div
                                data-testid="affiliate-code"
                                className="mt-2 inline-block font-mono tracking-widest text-navy-700 text-lg bg-white border border-navy-100 rounded-lg px-3 py-1.5"
                            >
                                {ref?.code || "…"}
                            </div>
                            <div className="mt-4">
                                <div className="text-[11px] uppercase tracking-widest text-navy-500">
                                    Share URL
                                </div>
                                <div className="mt-2 flex gap-2">
                                    <Input
                                        readOnly
                                        data-testid="affiliate-share-url"
                                        value={shareUrl}
                                        className="h-11 bg-white border-navy-100 font-mono text-xs"
                                    />
                                    <Button
                                        onClick={() => copy(shareUrl, "Share URL")}
                                        data-testid="affiliate-copy-url"
                                        className="rounded-full h-11 bg-navy-700 hover:bg-navy-800 text-cream"
                                    >
                                        <Copy size={14} className="mr-1.5" />
                                        Copy
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl bg-cream border border-navy-100 p-5 flex flex-col">
                            <div className="text-[11px] uppercase tracking-widest text-gold-600">
                                QR code &amp; promo kit
                            </div>
                            <p className="text-sm text-navy-500 mt-2 flex-1">
                                Open your full share modal to print your QR card,
                                email invites, or copy the branded link.
                            </p>
                            <Button
                                onClick={() => setModalOpen(true)}
                                data-testid="affiliate-open-toolkit-btn"
                                className="mt-3 rounded-full h-11 bg-gold-500 hover:bg-gold-600 text-navy-800 self-start px-5 font-semibold"
                            >
                                <QrCode size={14} className="mr-2" />
                                Open toolkit
                            </Button>
                        </div>

                        <div className="rounded-2xl bg-navy-700 text-cream border border-navy-800 p-5 flex flex-col">
                            <div className="text-[11px] uppercase tracking-widest text-gold-400">
                                Panda Deck
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <Package size={16} className="text-gold-400" />
                                <span className="text-sm font-semibold">
                                    Everything you need to post today
                                </span>
                            </div>
                            <p className="text-xs text-cream/70 mt-2 flex-1 leading-relaxed">
                                Logos, on-brand screenshots, and platform-specific
                                caption templates already personalised with your
                                referral link and code — pre-approved by the Perx
                                brand team.
                            </p>
                            <Button
                                onClick={downloadPandaDeck}
                                disabled={deckBusy}
                                data-testid="affiliate-download-deck-btn"
                                className="mt-3 rounded-full h-11 bg-gold-500 hover:bg-gold-600 text-navy-800 self-start px-5 font-semibold disabled:opacity-50"
                            >
                                <Download size={14} className="mr-2" />
                                {deckBusy ? "Preparing…" : "Download Panda Deck"}
                            </Button>
                            <div className="text-[10px] text-cream/50 mt-2">
                                ZIP · logos · captions · guidelines
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ReferralModal open={modalOpen} onClose={() => setModalOpen(false)} />
        </>
    );
}

function StatCard({ icon: Icon, label, value, blurb }) {
    return (
        <div className="rounded-2xl bg-white border border-navy-100 p-5">
            <div className="flex items-center gap-2 text-navy-500 text-xs uppercase tracking-widest">
                <Icon size={14} />
                {label}
            </div>
            <div className="font-display text-3xl text-navy-700 mt-2">{value}</div>
            <div className="text-[11px] text-navy-500 mt-1">{blurb}</div>
        </div>
    );
}

function money(cents, currency) {
    if (cents == null) return "—";
    return `${(currency || "hkd").toUpperCase()} ${(cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function PaymentSummaryCard({ payments }) {
    if (!payments) {
        return (
            <div className="rounded-3xl border border-navy-100 bg-white p-6 h-24 flex items-center text-sm text-navy-400">
                Loading payments…
            </div>
        );
    }
    const { summary, payout_ready: payoutReady } = payments;
    const currency = summary?.currency || "hkd";
    const next = summary?.next_payout;
    return (
        <div
            data-testid="affiliate-payment-summary"
            className="rounded-3xl border border-navy-100 bg-white p-6 lg:p-8"
        >
            <div className="flex items-center gap-2">
                <Wallet size={18} className="text-gold-600" />
                <h2 className="font-display text-2xl text-navy-700">
                    Your commissions
                </h2>
            </div>
            <p className="text-sm text-navy-500 mt-1">
                Perx pays affiliates on the 5th of every month for converted
                referrals in the previous calendar month.
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <div className="rounded-2xl bg-navy-700 text-cream border border-navy-800 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-gold-400">
                        Lifetime paid
                    </div>
                    <div className="font-display text-2xl mt-1">
                        {money(summary?.lifetime_amount, currency)}
                    </div>
                    <div className="text-[11px] text-cream/60 mt-1">
                        Across {summary?.paid_count || 0} payouts
                    </div>
                </div>
                <div className="rounded-2xl bg-cream border border-navy-100 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-navy-500">
                        Year to date
                    </div>
                    <div className="font-display text-2xl text-navy-700 mt-1">
                        {money(summary?.ytd_amount, currency)}
                    </div>
                    <div className="text-[11px] text-navy-500 mt-1">Since Jan 1</div>
                </div>
                <div className="rounded-2xl bg-cream border border-navy-100 p-4 flex flex-col">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-navy-500">
                        <Clock size={11} />
                        Next payout
                    </div>
                    <div className="font-display text-2xl text-navy-700 mt-1">
                        {next
                            ? money(next.amount, next.currency || currency)
                            : "—"}
                    </div>
                    <div className="text-[11px] text-navy-500 mt-1 flex-1">
                        {next
                            ? `${next.period_label || ""} · ${next.conversions || 0} conversions`
                            : "No commissions in progress"}
                    </div>
                </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                {!payoutReady ? (
                    <div className="flex items-center gap-2 text-xs text-gold-700">
                        <AlertCircle size={13} />
                        Add payout details so your next commission actually lands
                        somewhere.
                    </div>
                ) : (
                    <div className="text-xs text-navy-500">
                        Payout details on file — you&rsquo;re good to receive
                        commissions.
                    </div>
                )}
                <div className="flex gap-2">
                    <Link to="/affiliate/payments">
                        <Button
                            variant="outline"
                            data-testid="dash-payments-link"
                            className="rounded-full h-10 border-navy-200 text-navy-700 text-xs"
                        >
                            View ledger
                            <ArrowRight size={12} className="ml-1.5" />
                        </Button>
                    </Link>
                    <Link to="/affiliate/payout-details">
                        <Button
                            data-testid="dash-payout-details-link"
                            className="rounded-full h-10 bg-navy-700 hover:bg-navy-800 text-cream text-xs"
                        >
                            {payoutReady ? "Update payout details" : "Add payout details"}
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
